import React, { useCallback, useEffect } from 'react';

import { useMutation, UseMutationResult } from '@tanstack/react-query';

import { IFileDto } from '@/shared/interfaces';
import { FileEntity } from '@/hooks/upload-files/types';
import { useFiles } from '@/hooks/upload-files/useFiles';
import { mapIFileDto2FileEntity } from '@/hooks/upload-files/mapIFileDto2FileEntity';
import {
    sendDoctorFile,
    sendPatientFile,
} from '@/features/caregiverSpace/api/uploadFIles/sendFile';
import { deleteFile } from '@/features/caregiverSpace/api/uploadFIles/deleteFile';

type UploadFilesProps = {
    className?: string;
    initialFiles?: IFileDto[];
    senderType?: 'PATIENT' | 'CAREGIVER';
    fileConstraints?: { maxFileSizeMb?: number };
};

export type UploadFilesReturn = {
    files: FileEntity[];
    deleteFileOnServer: UseMutationResult<any, Error, string, unknown>;
    removeFileFromList: (identifier: string) => void;
    handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};
export const useUploadFiles = (props?: UploadFilesProps): UploadFilesReturn => {
    const initial = props?.initialFiles
        ? mapIFileDto2FileEntity(props.initialFiles)
        : [];

    const { files, appendFiles, setUploaded, setFailed, removeFile } = useFiles(
        {
            initialFiles: initial,
            maxFileSizeMb: props?.fileConstraints?.maxFileSizeMb,
        },
    );

    const uploadMutation = useMutation<string, Error, FileEntity>({
        mutationFn:
            props?.senderType == 'PATIENT' ? sendPatientFile : sendDoctorFile,
        onSuccess: (guid, fileEntity) => {
            setUploaded({ identifier: fileEntity.identifier, guid });
        },
        onError: (_err, fileEntity) => {
            setFailed(fileEntity.identifier);
        },
    });
    const deleteFileOnServer = useMutation<string, Error, string>({
        mutationFn: deleteFile,
    });

    const handleFileChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const list = e.target.files;
            if (!list) return;
            const newEntities = appendFiles(list);
            // kick off upload immediately
            newEntities.forEach((ent) => uploadMutation.mutate(ent));
        },
        [appendFiles, uploadMutation],
    );

    const removeFileFromList = useCallback(
        (identifier: string) => {
            const toDelete = files.find((f) => f.identifier === identifier);
            if (toDelete && toDelete.guid) {
                deleteFileOnServer.mutate(toDelete.guid);
            }
            removeFile(identifier);
        },
        [files, removeFile, deleteFileOnServer],
    );

    return {
        files,
        deleteFileOnServer,
        removeFileFromList,
        handleFileChange,
    };
};
